import { memo, useCallback, useMemo, useState } from 'react';
import { isString } from 'radash';
import {
  canUseNewTimelineForUnmigratedLiveEvent,
  doesClipWorkOnAlignedStreams,
  getClipBroadcastId,
  isClipAudiogram,
  isClipLayoutDefault,
  isFitAppliedForClip,
  isOriginalLayoutAvailable
} from '@goldcast/player-config/src/utils/clipUtils';
import { isLayoutAudiogram } from '@goldcast/player-config/src/utils/layout';
import { LayoutType } from '@goldcast/player-config/src/types/asset-types';
import MatchSpeakersCTA from '../MatchSpeakersCTA';
import useClipCustomiserLayoutPopup from '../useClipLayoutPopupOptions';
import ClipToggleOptions from './ClipToggleOptions';
import { useSceneTracks } from '../../../ClipNewTimeline/TimelineSegments/TimelineScenes/SceneOptionsMenu/SceneOptionsMenuSpeakers/useSceneTracks';
import ClipCustomizerMultipleSceneWarning from '../ClipCustomizerMultipleSceneWarning';
import ClipCustomizerMigrateNudge from '../ClipCustomizerMigrateNudge';
import { CLIP_CUSTOMIZER_WARNING } from '../../../ClipCustomizer/useClipCustomizerWarnings';
import ClipCustomizerLayoutCard from './ClipCustomizerLayoutCard';
import LayoutSegmentedControl from './LayoutSegmentedControl';
import PipCornerSelector from './PipCornerSelector';
import AspectRatioSelector from './AspectRatioSelector';
import {
  NEW_LAYOUT_CARD_ORDER,
  PipCorner,
  SPEAKER_POSITION_OPTIONS,
  SPEAKER_TILE_SHAPE_OPTIONS,
  SpeakerPosition,
  SpeakerTileShape
} from './constants';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { toggleHideBorders, toggleSpeakerLabels, isLayoutsWithVisionDataEnabled } from '@/stores/clip';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';

function ClipCustomizerNewLayoutPopup({ warnings }: Pick<CLIP_CUSTOMIZER_WARNING, 'warnings'>) {
  const { clipId, clipData } = useClipsContext();
  const broadcastId = getClipBroadcastId(clipData) ?? '';
  const scenes = useMemo(() => clipData.asset_metadata.scenes ?? [], [clipData.asset_metadata.scenes]);
  const firstSceneId = scenes[0]?.id ?? '';

  const { tracksToRender, handleTrackToggle } = useSceneTracks({
    broadcastId,
    clipId,
    sceneId: firstSceneId,
    scenes,
    compositeSegmentId: ''
  });

  // UI-only placeholders — no persisted magicLayout field yet (CR-10349 follow-up).
  const [speakerPosition, setSpeakerPosition] = useState<SpeakerPosition>('RIGHT');
  const [speakerTileShape, setSpeakerTileShape] = useState<SpeakerTileShape>('ROUND');
  const [pipCorner, setPipCorner] = useState<PipCorner>('BOTTOM_RIGHT');

  const onNameCardToggle = useCallback(
    (value: boolean) => {
      toggleSpeakerLabels(clipId, value);
    },
    [clipId]
  );

  const onBorderToggle = useCallback(
    (value: boolean) => {
      toggleHideBorders(clipId, !value);
    },
    [clipId]
  );

  const isOriginalAvailable = useMemo(() => {
    return isOriginalLayoutAvailable(clipData);
  }, [clipData]);

  const isSpeakerLayoutAvailable = useMemo(() => {
    if (
      doesClipWorkOnAlignedStreams(clipData) ||
      isLayoutsWithVisionDataEnabled(clipData) ||
      canUseNewTimelineForUnmigratedLiveEvent(clipData)
    ) {
      return true;
    }
    return false;
  }, [clipData]);

  const { isBorderDisabled } = useMemo(() => {
    const clipLayout = clipData.asset_metadata.layout;
    const _isLayoutAudiogram = isLayoutAudiogram(clipLayout);

    return { isBorderDisabled: _isLayoutAudiogram };
  }, [clipData.asset_metadata.layout]);

  const { showNameCard, isSpeakerLabelOn } = useMemo(() => {
    const sceneCount = clipData.asset_metadata.scenes?.length ?? 0;
    const hideNameCard = !(sceneCount > 1) && isClipLayoutDefault(clipData);
    return {
      showNameCard: !hideNameCard, // if there is only one scene then clip layout and scene layout are in sync
      isSpeakerLabelOn: !!clipData.asset_metadata.magicLayout?.showSpeakerLabels || isClipAudiogram(clipData)
    };
  }, [clipData]);

  const triggerSpeakerIdentification = useCallback((params = {}) => {
    EventBus.dispatch(CustomEvents.OpenSpeakersIdentification, params);
  }, []);

  const {
    showMatchSpeakersCTA,
    originalLayoutDisableReason,
    gridDisableReason,
    focusDisableReason,
    pipDisableReason,
    fullScreenDisableReason,
    fitFillDisabledReason
  } = useClipCustomiserLayoutPopup();

  const isFitApplied = useMemo(() => {
    return isFitAppliedForClip(clipData);
  }, [clipData]);

  const disabledSpeakerLabelToggle = useMemo(() => {
    return isClipAudiogram(clipData);
  }, [clipData]);

  const disableReasonByLayout = useMemo<Partial<Record<LayoutType, string | null>>>(
    () => ({
      DEFAULT: originalLayoutDisableReason,
      SPEAKER: fullScreenDisableReason,
      GRID: gridDisableReason,
      FOCUS: focusDisableReason,
      PIP: pipDisableReason,
      AUDIOGRAM: null
    }),
    [originalLayoutDisableReason, fullScreenDisableReason, gridDisableReason, focusDisableReason, pipDisableReason]
  );

  const visibleCards = useMemo(() => {
    return NEW_LAYOUT_CARD_ORDER.filter(layout => {
      if (layout === 'DEFAULT') {
        return isOriginalAvailable;
      }
      if (layout === 'AUDIOGRAM') {
        return true;
      }
      return isSpeakerLayoutAvailable;
    });
  }, [isOriginalAvailable, isSpeakerLayoutAvailable]);

  return (
    <div className="flex max-h-[80vh] w-[400px] flex-col gap-4 overflow-y-auto rounded-lg border border-slate-100 bg-white p-4 shadow">
      <div className="flex w-full justify-between py-1">
        <div className="text-base font-semibold">Layouts</div>
        <div></div>
      </div>
      <AspectRatioSelector />
      <ClipCustomizerMigrateNudge />
      <ClipCustomizerMultipleSceneWarning skipMargin />
      <div className="flex w-full flex-col gap-3">
        <div className="text-xs">Layouts</div>
        <div className="rounded-xl border border-slate-100 p-4 pb-2">
          <div className="grid grid-cols-2 gap-x-[10px] gap-y-[12px]">
            {visibleCards.map(layout => (
              <ClipCustomizerLayoutCard
                key={layout}
                magicLayoutOption={layout}
                disabled={isString(disableReasonByLayout[layout])}
                disabledReason={disableReasonByLayout[layout] ?? ''}
              />
            ))}
          </div>
          <div className="mt-6">
            <LayoutSegmentedControl
              label="Speaker position"
              options={SPEAKER_POSITION_OPTIONS}
              value={speakerPosition}
              onChange={setSpeakerPosition}
            />
            <LayoutSegmentedControl
              label="Speaker tile shape"
              options={SPEAKER_TILE_SHAPE_OPTIONS}
              value={speakerTileShape}
              onChange={setSpeakerTileShape}
            />
            <PipCornerSelector value={pipCorner} onChange={setPipCorner} />
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col gap-3">
        <div className="text-xs">Speakers</div>
        <div className="rounded-xl border border-slate-100 p-4 pb-1">
          {showMatchSpeakersCTA && (
            <MatchSpeakersCTA
              onClick={() =>
                triggerSpeakerIdentification({
                  isInlineIdentification: true,
                  layout: clipData.asset_metadata.layout
                })
              }
              warnings={warnings}
            />
          )}

          <ClipToggleOptions
            showNameCard={showNameCard}
            isSpeakerLabelOn={isSpeakerLabelOn}
            onNameCardToggle={onNameCardToggle}
            showBorder={!clipData.asset_metadata.hide_borders}
            onBorderToggle={onBorderToggle}
            isBorderDisabled={isBorderDisabled}
            isFitApplied={isFitApplied}
            fitFillDisabledReason={fitFillDisabledReason}
            disabledSpeakerLabelToggle={disabledSpeakerLabelToggle}
            tracks={tracksToRender}
            onToggleTrack={handleTrackToggle}
          />
        </div>
      </div>
    </div>
  );
}

export default memo(ClipCustomizerNewLayoutPopup);
